import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { cloud } from "@vitalsource/vstui/colors";
import styled, { withTheme } from "styled-components";
import { selectors as booksSelectors } from "../../../../../redux/books";
import { selectors as bridgeSelectors } from "../../../../../redux/bridge";
import { selectors as ancillarySelectors } from "../../../../../redux/ancillaries";
import { actions as uiLibraryActions } from "../../../../../redux/ui/library";
import { actions as bookActions } from "../../../../../redux/book";
import { actions as bridgeActions } from "../../../../../redux/bridge";
import Cover from "../../book/cover";
import { TextEllipsis } from "../../../../components/text-ellipsis";
import HeroIcons from "./hero-icons";
import { Button } from "@vitalsource/vstui";
import Book from "../../../library/book/licensed-book";
import { styledMedia } from "../../../../components/mediaquery/breakpoints";
import { withAllMatches } from "../../../../components/mediaquery/breakpoints";
import Slash from "../../../../components/hero-bg/index";
import CoursewareIcon from "../../../library/icons/courseware/overlay";
import { BookCoverIconOverlay } from "../../../library/book/BookIcons";
import { t } from "../../../../core/translate";
import { expirationMessage } from "../../expiration-message";
import { WhiteButton } from "../../../../components/custom-buttons/white-buttons";
import LibraryHelpers from "../../../../../stores/library-helpers";

const IMG_WIDTH = 250;
const IMG_PADDING = 30;

const StyledHeroBg = styled.div`
  margin-bottom: 40px;
  min-height: 415px;
  position: relative;
  width: 100%;
  background: ${cloud.getShade(1)};
  ${styledMedia.small`
    min-height: 375px;
    margin-bottom: 0px;
  `}
`;

const CoverLink = styled.a.attrs(props => ({
  href: `/#/books/${props.isbn}`
}))`
  ${styledMedia.small`
    display: inline-block;
    width: ${IMG_WIDTH}px;
  `}
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  margin-right: 35px;
  width: 250px;
`;

const BookCover = styled.div`
  height: inherit;
  position: relative;
  width: ${IMG_WIDTH}px;
  float: left;
  margin-right: ${IMG_PADDING}px;
  transition: all 0.6s cubic-bezier(0.165, 0.84, 0.44, 1);
  border-radius: 4px;
  overflow: hidden;

  &:hover {
    transform: scale(1.05, 1.05);
  }
`;

const BookTitle = styled.div`
  font-size: 3.8rem;
  font-weight: 500;
  color: #ffffff;
  padding: 10px 0;
`;

const BookAuthor = styled.div`
  font-weight: 400;
  font-size: 2.2rem;
  color: #ffffff;
  padding-bottom: 15px;
`;

const BookInfo = styled.div`
  flex: 1 ${styledMedia.small`
    display: none;
  `};

  #icons-expiration-wrapper {
    display: flex;
    #expiration-message {
      display: flex;
      align-items: center;
      color: #ffffff;
      font-size: 16px;
      margin-bottom: 20px;
      border-left: 1px solid ${cloud.getShade(4)};
      padding-left: 25px;
      margin-left: 4px;
      svg {
        margin-right: 8px;
      }
    }
  }
`;

const HeroContentCentered = styled.div`
  display: flex;
  margin: 0 auto;
  padding-top: 50px;
  position: relative;
  width: 65%;
  ${styledMedia.small`
    display: block;
    padding-top: 25px;
    text-align: center;
    width: 100%;
  `}
`;

const ButtonWrapper = styled.div`
  display: flex;
  button {
    margin-left: 20px;
  }
`;

class LibraryHero extends Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  clickCover = e => {
    e.preventDefault();
    this.props.launch("hero-cover");
  };

  clickContinueReading = e => {
    e.preventDefault();
    this.props.launch("hero-continue-reading");
  };

  renderMobileHero() {
    const { isbn } = this.props;
    return (
      <StyledHeroBg>
        <Slash height="172px" />
        <HeroContentCentered>
          <Book isbn={isbn} hero context="mobile-hero" index={0} />
        </HeroContentCentered>
      </StyledHeroBg>
    );
  }

  renderDesktopHero() {
    const {
      isbn,
      licensedBook,
      bridgeBook,
      accessCode,
      ancillarySummary,
      returnBook
    } = this.props;
    const book = { ...licensedBook, ...bridgeBook };
    if (book) {
      const { Icon, label } = expirationMessage(book);
      return (
        <StyledHeroBg>
          <Slash />
          <HeroContentCentered>
            <CoverLink isbn={isbn} onClick={this.clickCover}>
              <BookCover>
                <Cover bookTitle={book.title} width={IMG_WIDTH} isbn={isbn} />
                {accessCode && (
                  <BookCoverIconOverlay width={IMG_WIDTH} height="100%">
                    <CoursewareIcon />
                  </BookCoverIconOverlay>
                )}
              </BookCover>
            </CoverLink>
            <BookInfo>
              <BookTitle id={`title-${book.isbn}`}>
                <TextEllipsis
                  position="bottom"
                  toolTipText={book.title.decodeHTML()}
                  lines={2}
                >
                  <div>{book.title.decodeHTML()}</div>
                </TextEllipsis>
              </BookTitle>
              {book.author && (
                <BookAuthor>
                  <TextEllipsis
                    position="bottom"
                    toolTipText={book.author.decodeHTML()}
                    lines={1}
                  >
                    <div>{book.author.decodeHTML()}</div>
                  </TextEllipsis>
                </BookAuthor>
              )}
              <div id="icons-expiration-wrapper">
                <HeroIcons isbn={isbn} />

                {Icon && label && (
                  <div id="expiration-message">
                    {<Icon width={18} height={21} />}
                    {label}
                  </div>
                )}
              </div>
              <ButtonWrapper>
                <Button
                  color="white"
                  colorBehindButton={this.props.theme.color.base.getShade(8)}
                  href={`/#/books/${isbn}`}
                  onClick={this.clickContinueReading}
                  size="large"
                >
                  {t("library.continue-reading", "Continue Reading")}
                </Button>
                {book && book.returnable && (
                  <WhiteButton
                    size="large"
                    onClick={() => {
                      returnBook();
                      LibraryHelpers.returnBookToast(book.title, book.isbn);
                    }}
                  >
                    {t("library.return-book", "Return")}
                  </WhiteButton>
                )}
                <br />
                {accessCode &&
                  (accessCode.accessCode &&
                  accessCode.href &&
                  accessCode.revealed &&
                  ancillarySummary.accessCodeCount ? (
                    <Button
                      color="white"
                      colorBehindButton={this.props.theme.color.base.getShade(
                        8
                      )}
                      onClick={() => {
                        Fraction.Helpers.Locations.safeRedirect(
                          accessCode.href,
                          "_blank"
                        );
                      }}
                      size="large"
                    >
                      {t("eresources.launch-courseware", "Launch Courseware")}
                    </Button>
                  ) : (
                    !accessCode.revealed && (
                      <Button
                        color="white"
                        colorBehindButton={this.props.theme.color.base.getShade(
                          8
                        )}
                        onClick={() =>
                          this.props.openBookPanel(
                            "courseware",
                            book.title.decodeHTML()
                          )
                        }
                        size="large"
                      >
                        {t("onboarding.reveal-code", "Reveal Access Code")}
                      </Button>
                    )
                  ))}
              </ButtonWrapper>
            </BookInfo>
          </HeroContentCentered>
        </StyledHeroBg>
      );
    }
    return null;
  }

  render() {
    const { isSmall } = this.props;
    if (isSmall) {
      return this.renderMobileHero();
    }
    return this.renderDesktopHero();
  }
}

const mapStateToProps = (state, props) => ({
  accessCode: ancillarySelectors.getAccessCodesForVbid(state, props.isbn),
  ancillarySummary: ancillarySelectors.getAncillarySummaryForVbid(
    state,
    props.isbn
  ),
  licensedBook: booksSelectors.getBookByIsbn(state, props.isbn),
  bridgeBook: bridgeSelectors.getAsset(state, props.isbn)
});

const mapDispatchToProps = (dispatch, props) => ({
  launch: context =>
    dispatch(bookActions.launchLicensedBook(props.isbn, { context })),
  openBookPanel: (tab, title) =>
    dispatch(uiLibraryActions.openBookPanel(props.isbn, tab, title)),
  returnBook: isbn => dispatch(bridgeActions.returnAsset(props.isbn))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withAllMatches(withTheme(LibraryHero)));

LibraryHero.defaultProps = {
  author: "",
  title: ""
};

LibraryHero.propTypes = {
  author: PropTypes.string,
  isbn: PropTypes.string.isRequired,
  title: PropTypes.string
};
